import styled from "styled-components";
import {useEffect, useMemo, useState} from "react";
import {API} from "../API/api";
import useGlobalSettings from "../Provider/useGlobalSettings";
import {ViewTypes} from "../Provider/useGlobalSettingsContextProvider";
import {useNavigate, useParams} from "react-router-dom";
import { FaLungs } from "react-icons/fa";
import {IconType} from "react-icons";
import {CiTempHigh} from "react-icons/ci";
import {FaTemperatureHigh} from "react-icons/fa6";

const Styles = {
    Frame: styled.div`
        position: relative;
        padding-top: 20px;
        padding-bottom: 50px;
    `,
    Image: {
        Frame: styled.div`
            position: relative;
        `,
        Image: styled.img`
            width: 100%;
            //filter: saturate(0.4) brightness(.9);
            opacity: 0.8;
            z-index: -1;
        `,
        Name: styled.div`
            position: absolute;
            height: inherit;
            display: flex;
            justify-content: center;
            align-self: center;
            top: calc(50% - 50px);
            font-size: 50px;
            font-weight: bold;
            z-index: 10;
            width: 100%;
            filter: drop-shadow(4px 4px 10px rgba(0,0,0,0.5));
        `
    },
    Additional: styled.div`
        padding-top: 20px;
        display: flex;
        flex-direction: column;
        gap: 10px;
    `,
    Caption: styled.div<{ $focused: boolean }>`
        padding: 5px 10px;
        padding-right: 20px;
        background-color: silver;
        width: ${ p => p.$focused ? 'min( 90%, 500px )' : 'min-content' };
        color: black;
        border-top-right-radius: 15px;
        border-bottom-right-radius: 15px;
        cursor: pointer;
        &:hover {
            background-color: #eee;
            padding-left: ${ p => p.$focused ? undefined : '20px' };
        }
        transition: all 1s ease-in-out;
    `,
    Content: styled.div`
        padding-top: 10px;
        padding-left: 20px;
        padding-right: 20px;
        padding-bottom: 15px;
        text-align: left;
    `,
    Contents: {
        frame: styled.div`
            padding-bottom: 30px;
        `,
        name: styled.p`
            font-weight: bold;
            font-size: 1.2em;
            marign-bottom: 0;
            padding: 0;
        `,
        desc: styled.p`
            margin-top: 5px;
        `,
        img: styled.img`
            width: 100%;
            height: auto;
        `,
        icons: styled.div`
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
            margin-top: 10px;
        `,
        icon: styled.p`
            font-size: 0.8em;
        `
    }
}
const TempIcon = styled(FaTemperatureHigh)<{ $avg: string }>`
    color: ${ p => p.$avg === "high" ? "orangered" :
        p.$avg === "low" ? "aliceblue" :
        p.$avg === "fine" ? "lightgreen" :
        p.$avg === "careful" ? "orange" : undefined };
`;

interface iSystems {
    id: string;
    description: string;
}
interface iSystem {
    id: string;
    typ: string;
    name: string;
    images: string;
    objects?: string[];
    description: string;
    weather: string;
}
type ContentObject = {
    name: string;
    description: string;
    image?: string;
    icons?: {
        breathability?: boolean;
        temp?: {
            avg: "high" | "low" | "fine" | "careful";
            day?: {
                description: string;
            },
            night?: {
                description: string;
            }
        }
    }
}

type ContentText = undefined | string | string[] | ContentObject | ContentObject[];

const Content = ({ text }: { text: ContentText }) => {
    if( ! text  ){
        return <></>;
    }

    if( Array.isArray( text ) ){
        if( text?.length === 0 )
            return <>keine vorhanden</>;

        return <Styles.Content>
            { text
                .sort( (a,b) => typeof a === "string" ? 1 : typeof b === "string" ? -1 : a.name.localeCompare( b.name ) )
                .map( (item, index) =>
                typeof item === "string"
                    ? <Styles.Contents.desc key={ index }>{ item }</Styles.Contents.desc>
                    : <Styles.Contents.frame>
                        <Styles.Contents.name key={ index }>{ item.name }</Styles.Contents.name>
                        { item.icons &&
                            <Styles.Contents.icons>
                                <div style={{ display: "flex", gap: "10px" }}>
                                    <FaLungs style={{ color: item.icons?.breathability ? "lightgreen" : "orangered" }} />
                                    <TempIcon $avg={ item.icons?.temp?.avg || "" } />
                                </div>
                                { item.icons.temp && item.icons.temp.day && <Styles.Contents.icon>Tag: { item.icons.temp.day.description }</Styles.Contents.icon> }
                                { item.icons.temp && item.icons.temp.night && <Styles.Contents.icon>Nacht: { item.icons.temp.night.description }</Styles.Contents.icon> }
                            </Styles.Contents.icons>
                        }
                        <Styles.Contents.desc>{ item.description }</Styles.Contents.desc>
                        <Styles.Contents.img src={ item.image } />
                    </Styles.Contents.frame>
            )}
        </Styles.Content>
    }

    if( typeof text === "object" ){
        return <Styles.Content>
            <p><strong>{ text.name }</strong>: { text.description }</p>
        </Styles.Content>
    }

    return <Styles.Content>{ text }</Styles.Content>
}

const WholeSystem = ({ data } :{ data: iSystem }) => {

    const navigate = useNavigate();
    const { systemID } = useParams();

    const handles = {
        click: ( object: string ) => {
            void navigate( `/verse-guide/${ systemID }/${ object }` );
        }
    }

    return <Styles.Frame>
        <Styles.Image.Frame>
            <Styles.Image.Name>{ data.name || "unbekannt" }</Styles.Image.Name>
            <Styles.Image.Image src={ data.images } />
        </Styles.Image.Frame>

        <Styles.Additional>
            { data?.objects && Object.values( data.objects )
                .sort( (a, b) => a.localeCompare(b) )
                .map( (caption,idx) =>
                    <div key={ idx } >
                        <Styles.Caption $focused={ false }
                                        onClick={ () => handles.click( caption ) }>
                            { caption }
                        </Styles.Caption>
                    </div>
                )}
        </Styles.Additional>
    </Styles.Frame>
}
const AnyObject = ({ data } :{ data: iSystem }) => {

    const [focusedCaption, setFocusedCaption] = useState<number| null>(null)

    const handles = {
        selectCaption: (idx: number) => {
            setFocusedCaption( p => p === idx ? null : idx )
        }
    }

    const captions: Record<string, { title: string; tag: keyof iSystem }> = {
        global: { title: "Allgemeine Informationen", tag: "description" },
        weather: { title: "Wetterbericht", tag: "weather" },
        objects: { title: "Himmelskörper", tag: "objects" },
    }

    return <Styles.Frame>
        <Styles.Image.Frame>
            <Styles.Image.Name>{ data.name || "unbekannt" }</Styles.Image.Name>
            <Styles.Image.Image src={ data.images } />
        </Styles.Image.Frame>

        <Styles.Additional>
            { data && Object.values( captions )
                .map( (caption,idx) =>
                    <div key={ idx } >
                        <Styles.Caption $focused={ idx === focusedCaption }
                                        onClick={ () => handles.selectCaption( idx ) }>
                            { caption.title }
                        </Styles.Caption>
                        { idx === focusedCaption &&
                            <Content text={ data?.[ caption.tag ] }/> }
                    </div>
                )}
        </Styles.Additional>

    </Styles.Frame>;
}

const Display = {
    WholeSystem: WholeSystem,
    Object: AnyObject
}

export const VerseGuideSystem = () => {
    const { systemID, objectID } = useParams();
    const [system, setSystem] = useState<iSystem[]>([]);

    useEffect(() => {
        if( systemID )
            API.getVerseGuide.system( systemID )
                .then( response => {
                    setSystem( response.data )
                    // Actions.setViewTo( ViewTypes.VerseGuideSystem )
                })
    }, [systemID]);

    const wholeSystem = useMemo( () =>
            system.find( system => system.typ === "system" )
    , [system] )

    const focusedObject = useMemo( () =>
        system.find( system => system.typ !== "system" && system.name === objectID )
    , [system, systemID, objectID] )



    if( focusedObject )
        return <Display.Object data={ focusedObject } />
    if( wholeSystem )
        return <Display.WholeSystem data={ wholeSystem } />

    return <div>System not found</div>
};